import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import { getSysManagerContacts } from './sysManagerContactsApi.js';
import './SysManagerContacts.css';

const RESULTS_PER_PAGE = 25;

/** Convert one API contact into a safe UI row. */
function normaliseContact(rawContact) {
  const pupils = Array.isArray(rawContact?.pupils)
    ? rawContact.pupils
      .map((pupil) => ({
        pupilId: String(pupil?.pupilId || '').trim(),
        pupilName: String(pupil?.pupilName || '').trim(),
        relationship: String(pupil?.relationship || '').trim(),
      }))
      .filter((pupil) => pupil.pupilId && pupil.pupilName)
    : [];
  return {
    contactId: String(rawContact?.contactId || '').trim(),
    displayName: String(rawContact?.displayName || '').trim(),
    email: String(rawContact?.email || '').trim(),
    phoneNumber: String(rawContact?.phoneNumber || '').trim(),
    address: String(rawContact?.address || '').trim(),
    pupils,
  };
}

/** Display searchable parent and carer contact records. */
export default function SysManagerContacts() {
  const navigate = useNavigate();
  const permissions = userAppData?.sysManager || {};
  const authorised = Number(permissions.perms ?? 0) === 1 && permissions.contactsAdmin === true;
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredContacts = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return contacts;
    return contacts.filter((contact) => [
      contact.displayName,
      contact.email,
      contact.phoneNumber,
      contact.address,
      ...contact.pupils.flatMap((pupil) => [pupil.pupilName, pupil.relationship]),
    ].join(' ').toLowerCase().includes(searchValue));
  }, [contacts, searchText]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / RESULTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * RESULTS_PER_PAGE;
  const visibleContacts = filteredContacts.slice(pageStart, pageStart + RESULTS_PER_PAGE);
  const firstVisible = filteredContacts.length === 0 ? 0 : pageStart + 1;
  const lastVisible = Math.min(pageStart + visibleContacts.length, filteredContacts.length);

  useEffect(() => {
    if (!authorised) return;
    async function loadContacts() {
      setLoading(true);
      setLoadFailed(false);
      try {
        const response = await getSysManagerContacts();
        setContacts(response.items
          .map(normaliseContact)
          .filter((contact) => contact.contactId && contact.displayName)
          .sort((first, second) => first.displayName.localeCompare(second.displayName, 'en-GB', { sensitivity: 'base' })));
      } catch (error) {
        console.error('Failed to load System Manager contacts', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    }
    loadContacts();
  }, [authorised]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (!authorised) return <Navigate to='/app/sysmanager' replace />;

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_contacts_container'>
        <Header
          title='Contacts'
          subtitle='View parent and carer contact details and their linked pupils.'
          rightContent={<button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}><FaArrowLeft aria-hidden='true' /> Back to System Manager</button>}
        />

        <section className='sysManager_contacts_card'>
          <div className='sysManager_contacts_toolbar'>
            <div>
              <h2>Parents and carers</h2>
              <p>Showing {firstVisible}-{lastVisible} of {filteredContacts.length}{filteredContacts.length !== contacts.length ? ` filtered from ${contacts.length}` : ''} contacts</p>
            </div>
            <input
              aria-label='Search contacts'
              type='search'
              value={searchText}
              onChange={(event) => { setSearchText(event.target.value); setCurrentPage(1); }}
              placeholder='Search name, pupil, email, phone or address'
            />
          </div>

          {loading && <div className='sysManager_contacts_status'><CgSpinner className='sysManager_contacts_spinner' /><p>Loading contacts...</p></div>}
          {!loading && loadFailed && <div className='sysManager_contacts_status'><p>Contacts could not be loaded.</p></div>}
          {!loading && !loadFailed && filteredContacts.length === 0 && <div className='sysManager_contacts_status'><p>{contacts.length === 0 ? 'No contacts were found.' : 'No contacts match your search.'}</p></div>}

          {!loading && !loadFailed && visibleContacts.length > 0 && (
            <>
              <div className='sysManager_contacts_tableWrap'>
                <table className='sysManager_contacts_table'>
                  <thead><tr><th>Name</th><th>Pupils</th><th>Email</th><th>Phone</th><th>Home address</th></tr></thead>
                  <tbody>{visibleContacts.map((contact) => (
                    <tr key={contact.contactId}>
                      <td data-label='Name'><strong>{contact.displayName}</strong></td>
                      <td data-label='Pupils'>
                        {contact.pupils.length > 0 ? contact.pupils.map((pupil) => (
                          <span className='sysManager_contacts_pupil' key={pupil.pupilId}>{pupil.pupilName}{pupil.relationship ? ` (${pupil.relationship})` : ''}</span>
                        )) : 'None linked'}
                      </td>
                      <td data-label='Email'>{contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : 'Not set'}</td>
                      <td data-label='Phone'>{contact.phoneNumber ? <a href={`tel:${contact.phoneNumber}`}>{contact.phoneNumber}</a> : 'Not set'}</td>
                      <td data-label='Home address'>{contact.address || 'Not set'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <nav className='sysManager_contacts_pagination' aria-label='Contact table pages'>
                <button type='button' aria-label='Previous page' disabled={safeCurrentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}><FaChevronLeft aria-hidden='true' /></button>
                <span>Page {safeCurrentPage} of {totalPages}</span>
                <button type='button' aria-label='Next page' disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}><FaChevronRight aria-hidden='true' /></button>
              </nav>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
